import { useForm } from 'react-hook-form';
import { PasswordResetModel, UserProviderValue } from '../models';
import { PasswordResetSchema } from '../schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { logout, resetPassword } from '../services';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/user.context';

export function useResetPassword() {
   const navigate = useNavigate();
   const location = useLocation();
   const queryParams = new URLSearchParams(location.search);

   const { setUser, setIsAuthenticated } = useContext<UserProviderValue>(UserContext);

   const userPasswordResetForm = useForm<PasswordResetModel>({
      resolver: zodResolver(PasswordResetSchema),
      mode: 'onChange',
   });

   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      if (location.pathname === '/reset-password' && setIsAuthenticated) {
         setUser(undefined);
         setIsAuthenticated(false);
         logout();
      }

      const token = queryParams.get('token');
      if (!token) {
         navigate('/login');
      }
   }, [location.pathname]);

   const submitResetPassword = async (data: PasswordResetModel) => {
      setIsLoading(true);

      const resetResponse = await resetPassword({
         password: data.newPassword,
         resetToken: queryParams.get('token') as string,
      });

      if (resetResponse.error) {
         toast.error(resetResponse.error?.message || 'Failed to reset password');
         setIsLoading(false);
         if (resetResponse.error?.code === 401) {
            navigate('/login');
         }
         return;
      }

      toast.success('Password reset successfully');
      navigate('/login');

      setIsLoading(false);
   };

   return {
      userPasswordResetForm,
      isLoading,
      submitResetPassword,
   };
}
